'use client';
import type { StaticImageData } from 'next/image';
import { useTranslations } from 'next-intl';
import { forwardRef, type ReactNode, useRef, useState } from 'react';
import Img2ReferenceEnImage from '@/app/assets/imgs/char2v/img2reference-en.png';
import Img2ReferenceZhImage from '@/app/assets/imgs/char2v/img2reference-zh.png';
import { BaseLineClose } from '@/app/components/icons/BaseLineClose';
import { BaseLineEdit } from '@/app/components/icons/BaseLineEdit';
import { BaseLineMusic } from '@/app/components/icons/BaseLineMusic';
import { BaseLinePlus } from '@/app/components/icons/BaseLinePlus';
import { BaseLineReplace } from '@/app/components/icons/BaseLineReplace';
import { CutTailoring1 } from '@/app/components/icons/CutTailoring1';
import { Loading } from '@/app/components/icons/Loading';
import { ProductLineTransform } from '@/app/components/icons/ProductLineTransform';
import { ProductView } from '@/app/components/icons/ProductView';
import { SelectObject } from '@/app/components/icons/SelectObject';
import { CustomTooltip } from '@/app/components/shared/CustomTooltip';
import { ElementCard } from '@/app/components/shared/ElementCard';
import { ImageOperationDialog } from '@/app/components/shared/ImageOperationDialog';
import { ImageUploader } from '@/app/components/shared/ImageUploader';
import { ImageWithPlaceholder } from '@/app/components/shared/ImageWithPlaceholder';
import { CharImgCropModal } from '@/app/components/shared/ImgCropModals/CharImgCropModal';
import { ReferenceAudioPreviewDialog } from '@/app/components/shared/ReferenceAudioPreviewDialog';
import { Button } from '@/app/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/app/components/ui/dropdown-menu';
import {
  GLOBAL_LANGUAGES,
  MAX_AUDIO_REFERENCE_PROMPTS,
  MAX_REFERENCE_PROMPTS,
  Q4_REFERENCE_AUDIO_ACCEPT,
  Q4_REFERENCE_AUDIO_MAX_DURATION_SECONDS,
  Q4_REFERENCE_AUDIO_MAX_FILE_SIZE,
  Q4_REFERENCE_AUDIO_MIN_DURATION_SECONDS,
} from '@/app/constants';
import { useAudioUpload, useBatchCrop, useCheckEnableCreateElement, useDevice, useDragAndDrop } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import type {
  AudioUploadFile,
  ElementModality,
  ElementType,
  ImagePrompt,
  ImageUploadFile,
  ReferencePrompt,
  SourceAudio,
  SupportedLanguage,
  UploadImageType,
} from '@/app/types';
import { cn, convertViduV1ElementToElementType, getAudioReferenceCount, getImageAndElementCount } from '@/app/utils';
import { resizeSourceImage } from '@/app/utils/client';
import { useLocale } from '@/i18n/routing';
import {
  ReferencePromptAddCard,
  ReferencePromptAudioCard,
  ReferencePromptElementCard,
  ReferencePromptForbiddenCard,
  ReferencePromptImageCard,
  ReferencePromptUploadingCard,
} from './ReferencePromptCard';

// Legacy SEO input still uses the old ElementCard layout until that branch is removed.
const generateLocalizedImages = (): Record<SupportedLanguage, StaticImageData> => {
  const dynamicConfig = GLOBAL_LANGUAGES.filter((lang) => lang !== 'zh').reduce(
    (acc, lang) => {
      acc[lang] = Img2ReferenceEnImage;
      return acc;
    },
    {} as Record<string, StaticImageData>,
  );

  return {
    zh: Img2ReferenceZhImage,
    ...dynamicConfig,
  } as Record<SupportedLanguage, StaticImageData>;
};

const LOCALIZED_IMAGES: Record<'img2Reference', Record<SupportedLanguage, StaticImageData>> = {
  img2Reference: generateLocalizedImages(),
};

interface ReferencePromptsGridProps {
  className?: string;
  elementClassName?: string;
  referencePrompts: ReferencePrompt[];
  maxImageSize: number;
  onUpdateReference: (id: string, updates: Partial<ReferencePrompt>) => void;
  onRemoveReference: (id: string) => void;
  onUploadImage: (file: ImageUploadFile, onUploadStatusChange?: (status: ImageUploadFile['status']) => void) => void;
  onUploadAudio?: (file: AudioUploadFile) => void;
  /** 替换参考卡片的音频添加入口。 */
  audioSelectionTrigger?: ReactNode;
  renderAudioReplacementTrigger?: (id: string, audio: SourceAudio) => ReactNode;
  onToggleElementsSelector: (allowedModalities?: Array<ElementModality>) => void;
  onOpenElementCreateDialog: (initialImagePrompt: ImagePrompt, imageId: string) => void;
  onOpenElementViewDialog: (element: ElementType, editMode?: boolean) => void;
  replaceImage: (index: number, file: ImageUploadFile) => UploadImageType | null;
  handleSwapReferences: (sourceIdx: number, targetIdx: number) => void;
  allowTempMode?: boolean; // 是否允许临时模式（未登录时不上传，只保存临时图片）
  maxCount?: number; // 最大参考数量，如果不提供则使用 MAX_REFERENCE_PROMPTS
  allowAudio?: boolean;
  /** 是否显示“主体”添加入口；Q4 落地页只开放图片和音频。 */
  allowElements?: boolean;
  cardVariant?: 'legacy' | 'q2Pro';
}

export const ReferencePromptsGrid = forwardRef<HTMLDivElement, ReferencePromptsGridProps>(
  (
    {
      className,
      elementClassName,
      referencePrompts,
      maxImageSize,
      onUpdateReference,
      onRemoveReference,
      onUploadImage,
      onUploadAudio,
      audioSelectionTrigger,
      renderAudioReplacementTrigger,
      onToggleElementsSelector,
      onOpenElementCreateDialog,
      onOpenElementViewDialog,
      replaceImage,
      handleSwapReferences,
      allowTempMode = true,
      maxCount = MAX_REFERENCE_PROMPTS,
      allowAudio = false,
      allowElements = true,
      cardVariant = 'q2Pro',
    },
    ref,
  ) => {
    const t = useTranslations();
    const locale = useLocale();
    const { isMobile } = useDevice();
    const [uploadDropDownOpen, setUploadDropDownOpen] = useState(false);
    const [preparingCropIndex, setPreparingCropIndex] = useState<number | null>(null);
    const [previewAudio, setPreviewAudio] = useState<{
      id: string;
      name: string;
      src: string;
      duration: number;
    } | null>(null);
    const replaceAudioTargetRef = useRef<{ id: string; name: string; prompt: SourceAudio } | null>(null);
    const userInfo = useStore((state) => state.userInfo);
    const flyEndId = useStore((state) => state.flyEndId);
    const { selectAudio } = useAudioUpload({
      minDuration: Q4_REFERENCE_AUDIO_MIN_DURATION_SECONDS,
      maxDuration: Q4_REFERENCE_AUDIO_MAX_DURATION_SECONDS,
      maxSize: Q4_REFERENCE_AUDIO_MAX_FILE_SIZE,
      accept: Q4_REFERENCE_AUDIO_ACCEPT,
      allowTempMode,
      getMaxBatchFiles: () => Math.max(1, MAX_AUDIO_REFERENCE_PROMPTS - getAudioReferenceCount(referencePrompts)),
      onChange: (file) => onUploadAudio?.(file),
    });
    const { selectAudio: selectReplacementAudio } = useAudioUpload({
      minDuration: Q4_REFERENCE_AUDIO_MIN_DURATION_SECONDS,
      maxDuration: Q4_REFERENCE_AUDIO_MAX_DURATION_SECONDS,
      maxSize: Q4_REFERENCE_AUDIO_MAX_FILE_SIZE,
      accept: Q4_REFERENCE_AUDIO_ACCEPT,
      allowTempMode,
      getMaxBatchFiles: () => 1,
      onChange: (file) => {
        const target = replaceAudioTargetRef.current;
        if (!target) return;

        if (file.status === 'uploading') {
          onUpdateReference(target.id, {
            status: 'uploading',
            prompt: null,
          });
          return;
        }

        if (file.status === 'done' && file.file) {
          onUpdateReference(target.id, {
            status: 'done',
            prompt: file.file,
            name: target.name,
          });
          replaceAudioTargetRef.current = null;
          return;
        }

        onUpdateReference(target.id, {
          status: 'done',
          prompt: target.prompt,
          name: target.name,
        });
        replaceAudioTargetRef.current = null;
      },
    });

    const { checkCreateElementEnablePromise } = useCheckEnableCreateElement(); // 用于检查是否可以创建主体
    const { onDragStart, onDragOver, onDropCapture } = useDragAndDrop({ onReorder: handleSwapReferences }); // 拖动换位逻辑
    const imageAndElementRemainingCount = Math.max(0, maxCount - getImageAndElementCount(referencePrompts));
    const audioRemainingCount = Math.max(0, MAX_AUDIO_REFERENCE_PROMPTS - getAudioReferenceCount(referencePrompts));
    const canAddImageOrElement = imageAndElementRemainingCount > 0;
    const canAddAudio = allowAudio && !!onUploadAudio && audioRemainingCount > 0;
    const isQ2ProStyle = cardVariant === 'q2Pro';

    const updateImagePromptByIds = (imagePromptMap: { id: string; prompt: ImagePrompt }[]) => {
      imagePromptMap.forEach(({ id, prompt }) => {
        onUpdateReference(id, {
          prompt,
          reference: undefined, // 裁剪后图片已变化，清除 creation_id 引用
        });
      });
    };

    // 用于对图片选择主体范围
    const { selectModalImageSource, handleCloseSelectModal, handleOpenSelectModal, handleSaveChangedImagePrompts } =
      useBatchCrop({ maxImageSize, updateImagePromptByIds, allowTempMode });

    const handleClickCropButton = async (idx: number, prompt?: ImagePrompt | null) => {
      try {
        setPreparingCropIndex(idx);
        await handleOpenSelectModal(idx, prompt);
      } finally {
        setPreparingCropIndex(null);
      }
    };

    const renderRemoveButton = (id: string) => {
      return (
        <div
          className="flex size-6 cursor-pointer items-center justify-center rounded-full bg-system-white24 text-system-white backdrop-blur-[2px]"
          onClick={() => onRemoveReference(id)}
        >
          <BaseLineClose className="text-base" />
        </div>
      );
    };

    const renderUploadingCard = (key: string) => {
      if (isQ2ProStyle) {
        return <ReferencePromptUploadingCard key={key} id={key} label={t('create.createForm.upload.uploading')} />;
      }

      return (
        <div
          key={key}
          className={cn(
            'flex aspect-[96/125] h-[133px] max-h-[133px] min-h-[108px] w-full max-w-[106px] flex-col items-center justify-center gap-2 rounded-6 bg-system-black36 p-1 lg:h-auto',
            elementClassName,
          )}
        >
          <div className="flex w-full flex-1 flex-col items-center justify-center rounded-2 bg-system-black36">
            <Loading className="animate-spin text-2xl" />
            <div className="mt-2 break-all text-system-white48 text-xs">{t('create.createForm.upload.uploading')}</div>
          </div>
        </div>
      );
    };

    const renderImageCard = (id: string, prompt: ImagePrompt, name: string, idx: number) => {
      const isCropLoading = preparingCropIndex === idx;

      if (isQ2ProStyle) {
        return (
          <ReferencePromptImageCard
            key={id}
            id={id}
            ref={id === flyEndId ? ref : undefined}
            prompt={prompt}
            name={name}
            indexLabel={idx + 1}
            onDragStart={(e) => onDragStart(e, idx)}
            onDragOver={(e) => onDragOver(e)}
            onDropCapture={(e) => onDropCapture(e, idx)}
            deleteButton={renderRemoveButton(id)}
            maxImageSize={maxImageSize}
            replaceImage={(file) => replaceImage(idx, file)}
            allowTempMode={allowTempMode}
            isCropLoading={isCropLoading}
            onCrop={() => handleClickCropButton(idx, prompt)}
            onRemove={() => onRemoveReference(id)}
            onCreateElement={() => {
              checkCreateElementEnablePromise({ query: {} }).then(({ error }) => {
                if (error) return;
                onOpenElementCreateDialog(prompt, id);
              });
            }}
            contentClassName={id === flyEndId ? 'animate-fade-in opacity-0' : undefined}
          />
        );
      }

      return (
        <div
          key={id}
          className="relative cursor-grab"
          draggable={true}
          onDragStart={(e) => onDragStart(e, idx)}
          onDragOver={(e) => onDragOver(e)}
          onDropCapture={(e) => onDropCapture(e, idx)}
          ref={id === flyEndId ? ref : undefined}
        >
          <ElementCard
            className={cn('aspect-[96/125] h-[133px] max-h-[133px] min-h-[108px] w-full lg:h-auto', elementClassName)}
            imgsContentClassName={id === flyEndId ? 'animate-fade-in opacity-0' : undefined}
            type="image"
            name={name}
            imagePrompt={prompt}
            editSlot={
              <ImageOperationDialog
                image={prompt}
                triggerClassName="absolute top-1 right-1 hidden cursor-pointer items-center justify-center rounded-4 bg-[rgba(0,0,0,.5)] p-[1px] text-system-white text-xl sm:flex w-6 h-6 z-10"
                handleRemoveImage={() => onRemoveReference(id)}
                operations={[
                  {
                    key: 'select',
                    icon: isCropLoading ? <Loading className="animate-spin" /> : <SelectObject />,
                    text: t('create.createForm.upload.crop'),
                    onClick: () => handleClickCropButton(idx, prompt),
                    disabled: isCropLoading,
                  },
                ]}
              />
            }
            deleteSlot={renderRemoveButton(id)}
            hoverOperations={[
              <ImageUploader
                key="replace"
                maxCount={1}
                getSelectedImage={(url) => resizeSourceImage({ url, maxImageSize })}
                onChange={(file) => replaceImage(idx, file)}
                allowTempMode={allowTempMode}
              >
                <BaseLineReplace />
              </ImageUploader>,
              isCropLoading ? (
                <Loading key="select" className="animate-spin cursor-not-allowed" />
              ) : (
                <CutTailoring1
                  key="select"
                  className="cursor-pointer"
                  onClick={() => handleClickCropButton(idx, prompt)}
                />
              ),
            ]}
            slotAfterName={
              <CustomTooltip
                content={
                  <>
                    <div className="flex w-fit items-center gap-2 rounded-[100px] bg-[rgba(58,162,255,0.2)] px-2 py-1">
                      <ProductLineTransform className="text-[#4B96FF] text-sm" />
                      <span className="text-[#4B96FF] text-sm">{t('create.char2VideoForm.element.addToElement')}</span>
                    </div>
                    <p className="my-3 font-medium text-base text-white">
                      {t('create.char2VideoForm.element.addToElementTip')}
                    </p>
                    <ImageWithPlaceholder
                      src={LOCALIZED_IMAGES.img2Reference[locale as keyof typeof LOCALIZED_IMAGES.img2Reference].src}
                      alt=""
                      className="h-[160px] w-[296px] object-cover"
                    />
                  </>
                }
                variant="extend"
                contentClassName="w-[328px] p-4"
                sideOffset={8}
                disableHoverableContent={false}
                align="start"
                side="bottom"
                trigger="hover"
              >
                <ProductLineTransform
                  className="shrink-0 cursor-pointer text-base text-system-white opacity-40 hover:opacity-100"
                  onClick={() => {
                    checkCreateElementEnablePromise({ query: {} }).then(({ error }) => {
                      if (error) {
                        return;
                      }

                      onOpenElementCreateDialog(prompt, id);
                    });
                  }}
                />
              </CustomTooltip>
            }
          />
          {/* 拖动蒙层，防止图片被拽出 */}
          <div className="pointer-events-none absolute inset-0 opacity-0" />
        </div>
      );
    };

    const renderElementCard = (id: string, prompt: ElementType, idx: number, name: string) => {
      if (isQ2ProStyle) {
        return (
          <ReferencePromptElementCard
            key={id}
            id={id}
            element={prompt}
            name={name}
            onDragStart={(e) => onDragStart(e, idx)}
            onDragOver={(e) => onDragOver(e)}
            onDropCapture={(e) => onDropCapture(e, idx)}
            deleteButton={!isMobile && renderRemoveButton(id)}
            showActions={!isMobile}
            showTopRightSlot={!isMobile}
            canEdit={
              prompt?.type === 'user' &&
              prompt.creator_id === userInfo?.id &&
              !['approved', 'pending'].includes(prompt?.share_meta?.status || '')
            }
            onView={() => onOpenElementViewDialog(prompt)}
            onEdit={() => onOpenElementViewDialog(prompt, true)}
          />
        );
      }

      return (
        <div
          key={id}
          className="relative cursor-grab"
          draggable={true}
          onDragStart={(e) => onDragStart(e, idx)}
          onDragOver={(e) => onDragOver(e)}
          onDropCapture={(e) => onDropCapture(e, idx)}
        >
          <ElementCard
            className={cn('aspect-[96/125] h-[133px] max-h-[133px] min-h-[108px] w-full lg:h-auto', elementClassName)}
            type="element"
            element={prompt}
            onEdit={() => {
              onOpenElementViewDialog(prompt);
            }}
            deleteSlot={!isMobile && renderRemoveButton(id)}
            hoverOperations={
              isMobile
                ? []
                : [
                    prompt?.type === 'user' &&
                      prompt.creator_id === userInfo?.id &&
                      !['approved', 'pending'].includes(prompt?.share_meta?.status || '') && (
                        <BaseLineEdit
                          key="edit"
                          onClick={() => {
                            onOpenElementViewDialog(prompt, true);
                          }}
                        />
                      ),
                    <ProductView
                      key="view"
                      onClick={() => {
                        onOpenElementViewDialog(prompt);
                      }}
                    />,
                  ]
            }
          />
          {/* 拖动蒙层，防止图片被拽出 */}
          <div className="pointer-events-none absolute inset-0 opacity-0" />
        </div>
      );
    };

    const renderForbiddenCard = (id: string, name: string) => {
      if (isQ2ProStyle) {
        return <ReferencePromptForbiddenCard key={id} id={id} name={name} deleteButton={renderRemoveButton(id)} />;
      }

      return (
        <CustomTooltip
          key={id}
          content={t('create.char2VideoForm.elementForbidden.tooltip')}
          sideOffset={4}
          contentClassName="bg-[#525A69]"
          arrowClassName="text-[#525A69]"
          disableHoverableContent={false}
          trigger="hover"
        >
          <div>
            <ElementCard
              className={cn(
                'aspect-[96/125] h-[133px] max-h-[133px] min-h-[108px] w-full bg-system-white10 lg:h-auto',
                elementClassName,
              )}
              key={id}
              type="element"
              forbidden={true}
              element={{
                type: 'user',
                name,
              }}
              deleteSlot={renderRemoveButton(id)}
            />
          </div>
        </CustomTooltip>
      );
    };

    const renderAudioCard = (id: string, name: string, prompt: NonNullable<ReferencePrompt['prompt']>, idx: number) => {
      if (!('src' in prompt)) return null;
      const audioSrc = prompt.preview || prompt.src;
      const duration = prompt.duration || 0;

      if (isQ2ProStyle) {
        return (
          <ReferencePromptAudioCard
            key={id}
            id={id}
            name={name}
            replacementTrigger={renderAudioReplacementTrigger?.(id, prompt)}
            onDragStart={(e) => onDragStart(e, idx)}
            onDragOver={(e) => onDragOver(e)}
            onDropCapture={(e) => onDropCapture(e, idx)}
            deleteButton={renderRemoveButton(id)}
            onPlay={
              audioSrc
                ? () => {
                    setPreviewAudio({ id, name, src: audioSrc, duration });
                  }
                : undefined
            }
            onReplace={() => {
              replaceAudioTargetRef.current = { id, name, prompt };
              selectReplacementAudio();
            }}
          />
        );
      }

      return (
        <div
          key={id}
          className="relative cursor-grab"
          draggable={true}
          onDragStart={(e) => onDragStart(e, idx)}
          onDragOver={(e) => onDragOver(e)}
          onDropCapture={(e) => onDropCapture(e, idx)}
        >
          <div
            className={cn(
              'flex aspect-[96/125] h-[133px] max-h-[133px] min-h-[108px] w-full max-w-[106px] flex-col justify-between rounded-6 bg-system-black36 p-2 text-system-white lg:h-auto',
              elementClassName,
            )}
          >
            <div className="flex items-center justify-between gap-2">
              <BaseLineMusic className="shrink-0 text-lg text-system-white64" />
              {renderRemoveButton(id)}
            </div>
            <div className="min-w-0">
              <div className="truncate text-xs">{name}</div>
              <div className="mt-1 text-[10px] text-system-white48">
                {typeof prompt.duration === 'number'
                  ? `${prompt.duration.toFixed(1)}s`
                  : t('create.char2VideoForm.audio')}
              </div>
            </div>
            {audioSrc ? (
              <audio className="h-7 w-full" controls src={audioSrc}>
                <track kind="captions" />
              </audio>
            ) : (
              <div className="text-[10px] text-system-white32">{t('assets.selectedAudioNoSourceHint')}</div>
            )}
          </div>
          <div className="pointer-events-none absolute inset-0 opacity-0" />
        </div>
      );
    };

    const renderQ2ProAddReferenceCard = () => {
      return (
        <ReferencePromptAddCard
          className={elementClassName}
          imageSlot={
            canAddImageOrElement ? (
              <ImageUploader
                className="flex w-full cursor-pointer items-center justify-center gap-1 transition-colors hover:bg-system-white08"
                maxCount={imageAndElementRemainingCount}
                getSelectedImage={(url) => resizeSourceImage({ url, maxImageSize })}
                onChange={(file) => onUploadImage(file)}
                allowTempMode={allowTempMode}
              >
                <BaseLinePlus className="text-base text-system-white" />
                <span className="text-system-white64 text-xs">{t('create.char2VideoForm.element.addImages')}</span>
              </ImageUploader>
            ) : undefined
          }
          elementSlot={
            canAddImageOrElement && allowElements ? (
              <button
                type="button"
                className="flex w-full cursor-pointer items-center justify-center gap-1 transition-colors hover:bg-system-white08"
                onClick={() => onToggleElementsSelector(['image', 'text'])}
              >
                <BaseLinePlus className="text-base text-system-white" />
                <span className="text-system-white64 text-xs">
                  {t('create.char2VideoForm.element.addElementsShort')}
                </span>
              </button>
            ) : undefined
          }
          audioSlot={
            canAddAudio
              ? (audioSelectionTrigger ?? (
                  <button
                    type="button"
                    className="flex w-full cursor-pointer items-center justify-center gap-1 transition-colors hover:bg-system-white08"
                    onClick={selectAudio}
                  >
                    <BaseLinePlus className="text-base text-system-white" />
                    <span className="text-system-white64 text-xs">{t('create.char2VideoForm.audio')}</span>
                  </button>
                ))
              : undefined
          }
        />
      );
    };

    const addImageOrElementCard = (
      <>
        <div
          aria-label="upload"
          className={cn(
            'flex h-24 w-full max-w-[106px] flex-col items-center justify-evenly overflow-hidden rounded-6 bg-system-black36 px-2 py-0 sm:hidden',
            elementClassName,
          )}
        >
          {canAddImageOrElement && (
            <>
              <ImageUploader
                className="group flex h-fit w-full items-center justify-center gap-1 rounded-8 bg-transparent px-1 py-2 text-white text-xs hover:bg-system-white08"
                maxCount={imageAndElementRemainingCount}
                getSelectedImage={(url) => resizeSourceImage({ url, maxImageSize })}
                onChange={(file) => onUploadImage(file)}
                allowTempMode={allowTempMode}
              >
                <BaseLinePlus className="shrink-0 text-base opacity-[.32] group-hover:opacity-100" />
                <span className="break-all opacity-80 group-hover:opacity-100">
                  {t('create.char2VideoForm.element.addImages')}
                </span>
              </ImageUploader>
              {allowElements && (
                <>
                  <div className="h-[1px] w-[calc(100%-16px)] bg-system-white08" />
                  <Button
                    className="group flex h-fit w-full items-center justify-center gap-1 rounded-8 bg-transparent px-1 py-2 font-normal text-white text-xs hover:bg-system-white08"
                    size="original"
                    onClick={() => onToggleElementsSelector(['image', 'text'])}
                  >
                    <BaseLinePlus className="shrink-0 text-base opacity-[.32] group-hover:opacity-100" />
                    <span className="opacity-80 group-hover:opacity-100">
                      {t('create.char2VideoForm.element.addElementsShort')}
                    </span>
                  </Button>
                </>
              )}
            </>
          )}
          {canAddAudio && (
            <>
              {canAddImageOrElement && <div className="h-[1px] w-[calc(100%-16px)] bg-system-white08" />}
              <Button
                className="group flex h-fit w-full items-center justify-center gap-1 rounded-8 bg-transparent px-1 py-2 font-normal text-white text-xs hover:bg-system-white08"
                size="original"
                onClick={selectAudio}
              >
                <BaseLinePlus className="shrink-0 text-base opacity-[.32] group-hover:opacity-100" />
                <span className="opacity-80 group-hover:opacity-100">{t('create.char2VideoForm.audio')}</span>
              </Button>
            </>
          )}
        </div>
        <DropdownMenu open={uploadDropDownOpen} onOpenChange={setUploadDropDownOpen}>
          <DropdownMenuTrigger asChild>
            <div
              aria-label="upload"
              className={cn(
                'hidden h-24 flex-col items-center justify-center rounded-6 bg-system-black36 px-2 py-0 sm:flex',
                elementClassName,
              )}
            >
              <BaseLinePlus className="text-2xl" />
              <span className="mt-1 text-system-white32 text-xs">({t('elements.elementDialog.optional')})</span>
            </div>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="bg-system-hover01">
            {canAddImageOrElement && (
              <>
                <DropdownMenuItem onClick={(e) => e.preventDefault()}>
                  <ImageUploader
                    className="w-full text-xs"
                    maxCount={imageAndElementRemainingCount}
                    getSelectedImage={(url) => resizeSourceImage({ url, maxImageSize })}
                    onChange={(e) => {
                      onUploadImage(e, (status) => {
                        if (status === 'done') {
                          setUploadDropDownOpen(false);
                        }
                      });
                    }}
                    allowTempMode={allowTempMode}
                  >
                    {t('create.char2VideoForm.element.uploadImages')}
                  </ImageUploader>
                </DropdownMenuItem>
                {allowElements && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => onToggleElementsSelector(['image', 'text'])} className="text-xs">
                      {t('create.char2VideoForm.element.chooseElements')}
                    </DropdownMenuItem>
                  </>
                )}
              </>
            )}
            {canAddAudio && (
              <>
                {canAddImageOrElement && <DropdownMenuSeparator />}
                <DropdownMenuItem
                  onClick={() => {
                    selectAudio();
                    setUploadDropDownOpen(false);
                  }}
                  className="text-xs"
                >
                  {t('create.char2VideoForm.audio')}
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </>
    );

    const referenceCards = referencePrompts.map(({ prompt, status, type, name, id }, idx) => {
      if (status === 'uploading') {
        return renderUploadingCard(id);
      }
      if (type === 'image' && prompt && status === 'done') {
        return renderImageCard(id, prompt, name, idx);
      }
      if (type === 'element' && prompt && status !== 'forbidden') {
        return renderElementCard(id, convertViduV1ElementToElementType(prompt), idx, name);
      }
      if (type === 'audio' && prompt && status === 'done') {
        return renderAudioCard(id, name, prompt, idx);
      }
      if (status === 'forbidden') {
        return renderForbiddenCard(id, name);
      }
      return null;
    });

    return (
      <div
        className={cn(
          isQ2ProStyle
            ? 'isolate rounded-16 border border-[#35383F] border-dashed bg-system-black36 p-3'
            : 'isolate grid grid-cols-4 gap-2 py-2',
          className,
        )}
      >
        {isQ2ProStyle ? (
          <div className="grid auto-rows-[96px] grid-cols-4 gap-2">
            {referenceCards}
            {(canAddImageOrElement || canAddAudio) && renderQ2ProAddReferenceCard()}
          </div>
        ) : (
          <>
            {referenceCards}
            {(canAddImageOrElement || canAddAudio) && addImageOrElementCard}
          </>
        )}

        <CharImgCropModal
          imageSource={selectModalImageSource}
          referencePrompts={referencePrompts}
          onCancel={handleCloseSelectModal}
          onConfirm={handleSaveChangedImagePrompts}
          handleClickImagePrompt={handleOpenSelectModal}
          handleRemoveReference={onRemoveReference}
          maxImageSize={maxImageSize}
          onUploadImage={onUploadImage}
          replaceImage={replaceImage}
          handleExchange={handleSwapReferences}
          maxImageAndElementCount={maxCount}
        />
        <ReferenceAudioPreviewDialog audio={previewAudio} onOpenChange={(open) => !open && setPreviewAudio(null)} />
      </div>
    );
  },
);
